/**
 * Comment schema validation with Zod
 * @module schemas/comment
 */

import { z } from 'zod';
import { userSummarySchema } from './user.schema.js';
import { paginationSchema, uuidSchema } from './common.schema.js';

/** Create comment request schema */
export const createCommentRequestSchema = z.object({
  content: z
    .string()
    .min(1, 'Comment content is required')
    .max(10000, 'Comment must not exceed 10000 characters'),
  parentId: uuidSchema.nullable().optional(),
  mentions: z.array(uuidSchema).max(20, 'Maximum 20 mentions allowed').optional(),
});

/** Update comment request schema */
export const updateCommentRequestSchema = z.object({
  content: z
    .string()
    .min(1, 'Comment content is required')
    .max(10000, 'Comment must not exceed 10000 characters'),
  mentions: z.array(uuidSchema).max(20, 'Maximum 20 mentions allowed').optional(),
});

/** Comment response schema (recursive for replies) */
export const commentResponseSchema: z.ZodType<{
  id: string;
  objectId: string;
  parentId: string | null;
  content: string;
  mentions: z.infer<typeof userSummarySchema>[];
  author: z.infer<typeof userSummarySchema>;
  editedAt: string | null;
  createdAt: string;
  deletedAt: string | null;
  replies: unknown[];
}> = z.lazy(() =>
  z.object({
    id: uuidSchema,
    objectId: uuidSchema,
    parentId: uuidSchema.nullable(),
    content: z.string(),
    mentions: z.array(userSummarySchema),
    author: userSummarySchema,
    editedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    deletedAt: z.string().datetime().nullable(),
    replies: z.array(commentResponseSchema),
  })
);

/** Comment list response schema */
export const commentListResponseSchema = z.object({
  data: z.array(commentResponseSchema),
  pagination: paginationSchema,
});

/** List comments query params */
export const listCommentsQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
  includeDeleted: z.coerce.boolean().default(false),
});

// =============================================================================
// Type Inference
// =============================================================================

export type CreateCommentRequestInput = z.infer<typeof createCommentRequestSchema>;
export type UpdateCommentRequestInput = z.infer<typeof updateCommentRequestSchema>;
export type CommentResponseInput = z.infer<typeof commentResponseSchema>;
export type CommentListResponseInput = z.infer<typeof commentListResponseSchema>;
export type ListCommentsQueryInput = z.infer<typeof listCommentsQuerySchema>;
